import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Sends postMessage events to a parent window when the SPA is embedded in an iframe.
 *
 * Events emitted:
 *   { type: 'storefront:ready' }             — on mount
 *   { type: 'storefront:resize', height: n } — when content height changes
 *
 * Only active when window.top !== window.self (i.e. inside an iframe).
 */
export function useEmbedMessaging(): void {
  const location = useLocation();
  const isEmbedded = window.top !== window.self;

  // Signal ready on mount
  useEffect(() => {
    if (!isEmbedded) return;
    window.parent.postMessage({ type: 'storefront:ready' }, '*');
  }, [isEmbedded]);

  // Resize on route change + content mutations via ResizeObserver
  useEffect(() => {
    if (!isEmbedded) return;

    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'storefront:resize', height }, '*');
    };

    // Send immediately on route change
    sendHeight();

    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.documentElement);

    return () => observer.disconnect();
  }, [isEmbedded, location.pathname]);
}
